<template>
  <!--
    ArtifactDetail — WHAT: the DETAIL half of every list→detail flow. It fetches
    ONE record (by recordId, from artifact.detailUrl) and renders that artifact
    type's declared `sections` as horizontal TABS (Data Source | Aggregation | …),
    one section body visible at a time, full-width. The shell OWNS: load/reload,
    inline name editing, debounced autosave (the `patch` contract), and save-state.
    Section bodies OWN: rendering + emitting field patches. This split lets seven
    differently-shaped artifacts share one editor.

    WHERE: rendered by MetricStudio.vue when a record is selected (selectedId set),
    for the active (non-fullManager) artifact. Parent = MetricStudio.vue.

    CONTRACT
      props:
        artifact (Object, req)         — the registry entry (sections, urls,
          idField, nameField, accent).
        recordId (Number|String, req)  — which record to load; changing it reloads.
        context (Object)               — shared reference data loaded once by the
          shell (attributes, domains, metrics, opportunity primitives/flags),
          re-exposed to sections as `context` with artifactKey mixed in.
      emits:
        back()          — the "← {label}" button was clicked.
        saved(record)   — an autosave PATCH succeeded (parent refreshes ref data).
      Each section body is passed { record, artifact, context } and may emit:
        patch(partial)  — merged into record + debounced PATCH by applyPatch.
        refresh()       — triggers reload() (out-of-band change, e.g. a DA edit).

    NOTES: autosave debounces 500ms (queueSave→flush); the name field debounces
    separately and no-ops when unchanged. Sections must guard initial hydration so
    mounting doesn't emit a spurious patch (see the section contract in README).
    A section is wrapped in a titled white card UNLESS its registry entry sets
    selfChrome:true (join builder / viz / opportunity panels draw their own card).
    :key="activeSection" forces a fresh section instance per tab switch. Switching
    artifact type (watch) resets to the first section. sectionIcon falls back to
    'sliders'; icons must be registered in src/main.js.
  -->
  <div class="flex flex-col h-full min-h-0" style="background:#F9FAFB;">
    <div v-if="loading" class="p-6 text-xs text-gray-500">Loading…</div>
    <div v-else-if="error" class="p-6 text-xs text-red-600">{{ error }}</div>

    <template v-else-if="record">
      <!-- header (full-width) -->
      <div class="flex items-center justify-between gap-4 px-6 pt-4 pb-3 bg-white border-b border-gray-200">
        <div class="flex items-center gap-3 min-w-0 flex-1">
          <button
            class="inline-flex items-center gap-1.5 text-xs font-medium text-gray-600 bg-white border border-gray-200 rounded-lg px-3 py-2 hover:bg-gray-50 flex-shrink-0"
            @click="$emit('back')"
          >← {{ artifact.label }}</button>
          <span class="font-mono text-xs text-gray-400 flex-shrink-0">#{{ record[artifact.idField] }}</span>
          <input
            v-model="nameProxy"
            class="text-lg font-bold tracking-tight text-gray-800 bg-transparent border border-transparent hover:border-gray-200 focus:border-blue-400 focus:bg-white rounded px-1.5 py-0.5 w-full min-w-0 focus:outline-none"
            :placeholder="`${artifact.singular} name`"
            @input="onNameInput"
            @blur="flushName"
            @keydown.enter="$event.target.blur()"
          />
        </div>
        <span class="inline-flex items-center gap-1.5 font-mono text-[11px] flex-shrink-0" :style="{ color: saveState.color }">
          <span class="w-1.5 h-1.5 rounded-full" :style="{ background: saveState.color }"></span>{{ saveState.label }}
        </span>
      </div>

      <!-- section TAB BAR (horizontal) -->
      <div class="flex items-center gap-1 px-6 bg-white border-b border-gray-200 overflow-x-auto flex-shrink-0">
        <button
          v-for="sec in sections"
          :key="sec.key"
          class="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium border-b-2 -mb-px whitespace-nowrap transition-colors"
          :class="activeSection === sec.key
            ? 'border-gray-900 text-gray-900'
            : 'border-transparent text-gray-500 hover:text-gray-700'"
          @click="activeSection = sec.key"
        >
          <font-awesome-icon :icon="['fas', sectionIcon(sec.key)]" class="text-[10px]" :style="activeSection === sec.key ? { color: artifact.accent } : {}" />
          {{ sec.label }}
        </button>
      </div>

      <!-- active section body (full-width, scrolls).
           Sections flagged selfChrome draw their own white panels (the join
           builder / viz panel); everything else is wrapped in a white card so
           bare structured editors don't float on the gray background. -->
      <div class="flex-1 min-h-0 overflow-y-auto ms2-scroll px-6 pt-5 pb-24">
        <template v-if="activeMeta">
          <!-- self-chromed: render bare (but still lead with the in-UI help) -->
          <template v-if="activeMeta.selfChrome">
            <div
              v-if="sectionHelp"
              class="flex items-start gap-2.5 mb-4 px-3.5 py-2.5 rounded-lg border text-xs leading-relaxed"
              :style="{ background: artifact.accent + '0D', borderColor: artifact.accent + '33', color: '#374151' }"
            >
              <font-awesome-icon :icon="['fas', 'circle-info']" class="mt-0.5 flex-shrink-0" :style="{ color: artifact.accent }" />
              <span>{{ sectionHelp }}</span>
            </div>
            <component
              :is="activeMeta.component"
              :key="activeSection"
              :record="record"
              :artifact="artifact"
              :context="sharedContext"
              @patch="applyPatch"
              @refresh="reload"
            />
          </template>

          <!-- everything else: white card with a titled header -->
          <section v-else class="bg-white border border-gray-200 rounded-xl overflow-hidden">
            <div class="flex items-center gap-2.5 px-5 py-3.5 border-b border-gray-100">
              <span
                class="inline-flex items-center justify-center w-7 h-7 rounded-lg flex-shrink-0"
                :style="{ background: artifact.accent + '14', color: artifact.accent }"
              >
                <font-awesome-icon :icon="['fas', sectionIcon(activeSection)]" class="text-xs" />
              </span>
              <div class="min-w-0">
                <div class="text-[13px] font-semibold text-gray-800">{{ activeMeta.label }}</div>
                <p v-if="activeMeta.description" class="text-[11px] text-gray-400 truncate">{{ activeMeta.description }}</p>
              </div>
            </div>
            <div class="p-5">
              <!-- IN-UI HELP: how to use this tab, shown above the editor -->
              <div
                v-if="sectionHelp"
                class="flex items-start gap-2.5 mb-4 px-3.5 py-2.5 rounded-lg border text-xs leading-relaxed"
                :style="{ background: artifact.accent + '0D', borderColor: artifact.accent + '33', color: '#374151' }"
              >
                <font-awesome-icon :icon="['fas', 'circle-info']" class="mt-0.5 flex-shrink-0" :style="{ color: artifact.accent }" />
                <span>{{ sectionHelp }}</span>
              </div>
              <component
                :is="activeMeta.component"
                :key="activeSection"
                :record="record"
                :artifact="artifact"
                :context="sharedContext"
                @patch="applyPatch"
                @refresh="reload"
              />
            </div>
          </section>
        </template>
      </div>
    </template>
  </div>
</template>

<script>
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome';
import { SECTION_ICON } from './artifactRegistry.js';

// Section body components (each is a thin wrapper reusing existing Studio panels/
// builder, or a small structured editor). Registered globally here so the
// registry can name them as strings.
import MetricConfigSection from '../sections/MetricConfigSection.vue';
import MetricReportSection from '../sections/MetricReportSection.vue';
import DomainSection from '../sections/DomainSection.vue';
import OpportunitySection from '../sections/OpportunitySection.vue';
import MethodologySection from '../sections/MethodologySection.vue';
import ExpectationSection from '../sections/ExpectationSection.vue';
import MetricDefinitionSection from '../sections/MetricDefinitionSection.vue';
import AuditSection from '../sections/AuditSection.vue';
import FlagPresentationSection from '../sections/FlagPresentationSection.vue';
import FlagDataSourceSection from '../sections/FlagDataSourceSection.vue';
import WorklistMetaSection from '../sections/WorklistMetaSection.vue';
import WorklistDataSourceSection from '../sections/WorklistDataSourceSection.vue';
import WorklistColumnsSection from '../sections/WorklistColumnsSection.vue';
import InsightPlacementSection from '../sections/InsightPlacementSection.vue';
import InsightDataSourceSection from '../sections/InsightDataSourceSection.vue';
import InsightVizSection from '../sections/InsightVizSection.vue';
import StructuredMetaSection from '../sections/StructuredMetaSection.vue';
import StructuredPrimitivesSection from '../sections/StructuredPrimitivesSection.vue';
import StructuredPromptSection from '../sections/StructuredPromptSection.vue';
import StructuredOutputSection from '../sections/StructuredOutputSection.vue';
import AttributeSqlSection from '@/components/Admin/MetricStudio/sections/AttributeSqlSection.vue';
import AttributeHistorySection from '@/components/Admin/MetricStudio/sections/AttributeHistorySection.vue';
import StructuredTestSection from '../sections/StructuredTestSection.vue';

export default {
  name: 'ArtifactDetail',
  components: {
    FontAwesomeIcon,
    MetricConfigSection, MetricReportSection, DomainSection, OpportunitySection,
    MethodologySection, ExpectationSection, MetricDefinitionSection, AuditSection,
    FlagPresentationSection, FlagDataSourceSection,
    WorklistMetaSection, WorklistDataSourceSection, WorklistColumnsSection,
    InsightPlacementSection, InsightDataSourceSection, InsightVizSection,
    StructuredMetaSection, StructuredPrimitivesSection, StructuredPromptSection, StructuredOutputSection,
    StructuredTestSection,
    AttributeSqlSection, AttributeHistorySection,
  },
  props: {
    artifact: { type: Object, required: true },
    recordId: { type: [Number, String], required: true },
    // shared reference data loaded once by the parent shell (attributes, domains,
    // metrics, flags, opportunity primitives) — passed to every section as context.
    context: { type: Object, default: () => ({}) },
  },
  emits: ['back', 'saved'],
  data() {
    return {
      loading: true,
      error: null,
      record: null,
      saving: false,
      savedAt: null,
      nameProxy: '',
      activeSection: null,
    };
  },
  computed: {
    // Sections may be a static array OR a function of the record (e.g. the
    // structured artifact returns different tabs per config.executor). Resolve
    // both here so the whole detail shell is executor-aware for free.
    sections() {
      const s = this.artifact.sections;
      const resolved = typeof s === 'function' ? s(this.record) : s;
      return resolved || [];
    },
    sharedContext() {
      // Sections may need to trigger cross-section reloads or read shared data.
      return { ...this.context, artifactKey: this.artifact.key };
    },
    activeMeta() {
      return this.sections.find((s) => s.key === this.activeSection) || null;
    },
    // In-UI help text for the active tab: the richer `help`, else the short
    // `description`. Rendered as a callout above the section body.
    sectionHelp() {
      const m = this.activeMeta;
      return m ? (m.help || m.description || '') : '';
    },
    saveState() {
      if (this.saving) return { color: '#F59E0B', label: 'Saving…' };
      return { color: '#10B981', label: 'Saved · auto' };
    },
  },
  watch: {
    recordId() { this.load(); },
    artifact() {
      // switching artifact type resets to its first section tab
      this.activeSection = this.sections[0] ? this.sections[0].key : null;
    },
    // The resolved section set can change WITHOUT the artifact changing — e.g.
    // toggling config.executor swaps the structured tabs. Keep activeSection
    // valid: if the current tab no longer exists, fall back to the first.
    sections(secs) {
      if (!secs.length) { this.activeSection = null; return; }
      if (!secs.some((s) => s.key === this.activeSection)) {
        this.activeSection = secs[0].key;
      }
    },
  },
  async mounted() { await this.load(); },
  methods: {
    sectionIcon(key) { return SECTION_ICON[key] || 'sliders'; },

    async load() {
      this.loading = true;
      this.error = null;
      try {
        const { data } = await axios.get(this.artifact.detailUrl(this.recordId));
        this.record = data;
        this.nameProxy = data[this.artifact.nameField] || '';
        this._savedName = this.nameProxy;
        // sections resolve off the loaded record; seed the first tab now.
        if (!this.activeSection || !this.sections.some((s) => s.key === this.activeSection)) {
          this.activeSection = this.sections[0] ? this.sections[0].key : null;
        }
      } catch (e) {
        this.error = `Failed to load: ${e?.response?.data?.detail || e.message}`;
      } finally {
        this.loading = false;
      }
    },
    async reload() { await this.load(); },

    applyPatch(patch, options = {}) {
      if (!patch || typeof patch !== 'object') return;
      if (options.immediate) {
        this.flushImmediate(patch, options);
        return;
      }
      this.record = { ...this.record, ...patch };
      this.queueSave(patch);
    },
    queueSave(patch) {
      this._pending = { ...(this._pending || {}), ...patch };
      clearTimeout(this._saveTimer);
      this._saveTimer = setTimeout(() => this.flush(), 500);
    },
    async persist(patch) {
      this.saving = true;
      try {
        const { data } = await axios.patch(this.artifact.detailUrl(this.record[this.artifact.idField]), patch);
        this.record = data;
        this.savedAt = Date.now();
        this.$emit('saved', this.record);
        return data;
      } finally {
        this.saving = false;
      }
    },
    async flushImmediate(patch, options) {
      clearTimeout(this._saveTimer);
      const pending = this._pending;
      this._pending = null;
      try {
        const saved = await this.persist({ ...(pending || {}), ...patch });
        if (options.onSuccess) options.onSuccess(saved);
      } catch (e) {
        console.error('Save failed:', e?.response?.data || e.message);
        if (options.onError) options.onError();
      }
    },
    async flush() {
      clearTimeout(this._saveTimer);
      const patch = this._pending;
      this._pending = null;
      if (!patch || !Object.keys(patch).length) return;
      try {
        await this.persist(patch);
      } catch (e) {
        console.error('Autosave failed:', e?.response?.data || e.message);
      }
    },

    onNameInput() {
      clearTimeout(this._nameTimer);
      this._nameTimer = setTimeout(() => this.flushName(), 500);
    },
    flushName() {
      clearTimeout(this._nameTimer);
      const v = (this.nameProxy || '').trim();
      if (!v || v === this._savedName) return;
      this._savedName = v;
      this.applyPatch({ [this.artifact.nameField]: v });
    },
  },
};
</script>

<style scoped>
.ms2-scroll::-webkit-scrollbar { width: 10px; height: 10px; }
.ms2-scroll::-webkit-scrollbar-thumb { background: #D1D5DB; border-radius: 10px; border: 3px solid transparent; background-clip: content-box; }
.ms2-scroll::-webkit-scrollbar-track { background: transparent; }
</style>
